import { createApi } from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";
import { baseQueryWithLogout } from "../../app/query";
import type {
  CreatePurchaseParameters,
  CreatePurchaseResponse,
  GetProductsParameters,
  GetProductsResponse,
  GetTransactionParameters,
  GetTransactionResponse,
} from "./types";
import {
  isFetchBaseQueryError,
  isRTKQueryErrorWrapper,
} from "@utils/queryUtils.ts";

export const purchaseApi = createApi({
  reducerPath: "purchaseApi",
  baseQuery: baseQueryWithLogout,
  tagTypes: ["CreatePurchaseResponse"],
  endpoints: (builder) => ({
    getProducts: builder.query<GetProductsResponse, GetProductsParameters>({
      query: () => ({ url: "/v2/products", method: "GET" }),
    }),
    createPurchase: builder.mutation<
      CreatePurchaseResponse,
      CreatePurchaseParameters
    >({
      query: (params) => ({
        url: "/v2/pay",
        method: "POST",
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          window.location.assign(data.href);
        } catch (err) {
          if (isRTKQueryErrorWrapper(err) && isFetchBaseQueryError(err.error)) {
            if (
              typeof err.error.status === "number" &&
              err.error.status >= 500
            ) {
              Sentry.captureException(
                new Error("Purchase FetchBaseQuery error"),
                {
                  extra: { error: err },
                },
              );
            }
          } else {
            if (err instanceof Error) {
              Sentry.captureException(err);
            } else {
              Sentry.captureException(new Error("Purchase API unknown error"), {
                extra: { error: err },
              });
            }
          }
        }
      },
    }),
    getTransaction: builder.query<
      GetTransactionResponse,
      GetTransactionParameters
    >({
      query: (params) => ({
        url: `/v2/pay/${params.transaction_id}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useCreatePurchaseMutation,
  useGetTransactionQuery,
} = purchaseApi;
